import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { alertsApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";
import { DEGRADED_ANALYSES_AGGREGATE_LABEL } from "../components/degradedAnalysisVocabulary";

const SEV = {
  warning:  { bg: "bg-amber-50",  border: "border-amber-200",  text: "text-amber-800",  badge: "bg-amber-100  text-amber-800  border-amber-200",  label: "אזהרה",  icon: "⚠️" },
  critical: { bg: "bg-red-50",    border: "border-red-200",    text: "text-red-800",    badge: "bg-red-100   text-red-800    border-red-200",    label: "קריטי",  icon: "🚨" },
};

const TYPE_LABELS = {
  failure_rate:    "שיעור כשל",
  pending_backlog: "צבר ממתין",
  cost_budget:     "תקציב עלות",
  low_confidence:  "ביטחון AI נמוך",
  waiting_timeout: "קריאות תקועות בהמתנה",
  high_rejection:  "שיעור דחייה גבוה",
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
}

/**
 * The provenance of the `ביטחון ממוצע` tile: how many of the analyses that mean is taken over are
 * fallback content rather than a model's own answer.
 *
 * `avg_confidence` is a statistic of whichever adapter answered, and on a build with no
 * `ANALYSIS_PROVIDER` every analysis behind it is the deterministic mock's — a number derived from
 * the ticket's own text. The tile prints it as a measurement of the fleet's AI, so the note names
 * that figure in its own text (a note naming nothing would let a reader attach it to whichever
 * number they looked at first) and states the two counts the server sent.
 *
 * It renders nothing when the window declares no fallback content, so a fleet of real analyses is
 * not qualified, and nothing when the payload predates the counts, so an absent declaration never
 * reads as "none". The tile, the alert cards and the rest of the row render either way.
 */
function ConfidenceProvenance({ total, degraded }) {
  if (!degraded || !total) return null;
  return (
    <p
      className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      data-testid="alerts-confidence-provenance"
      title={`ביטחון ממוצע — מחושב מ-${total} ניתוחים שמורים, מתוכם ${degraded} אינם אבחון אמיתי`}
    >
      ביטחון ממוצע — <span className="font-semibold">{degraded}/{total}</span> {DEGRADED_ANALYSES_AGGREGATE_LABEL}
    </p>
  );
}

export default function AlertsPage() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [configDraft, setConfigDraft] = useState(null);
  const [dispatchMsg, setDispatchMsg] = useState(null);

  const { data: ev, isLoading: evLoading, isError: evFailed } = useQuery({
    queryKey: ["alerts-eval"],
    queryFn: alertsApi.evaluate,
    staleTime: 60_000,
  });

  const { data: config, isLoading: configLoading, isError: configFailed } = useQuery({
    queryKey: ["alerts-config"],
    queryFn: alertsApi.getConfig,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (config && !configDraft) setConfigDraft(config);
  }, [config, configDraft]);

  const dispatchMutation = useMutation({
    mutationFn: alertsApi.dispatch,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["alerts-eval"] });
      if (res.ticket_numbers.length > 0) {
        setDispatchMsg(`נוצרו קריאות: #${res.ticket_numbers.join(", #")}`);
      } else if (res.alerts.length === 0) {
        setDispatchMsg("אין התראות פעילות — לא נוצרו קריאות");
      } else {
        setDispatchMsg("התראות זוהו — לא נוצרו קריאות חדשות (cooldown פעיל, או שכבר קיימת קריאת התראה פתוחה מאותו סוג)");
      }
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בבדיקת התראות", "error"),
  });

  const configMutation = useMutation({
    mutationFn: alertsApi.setConfig,
    onSuccess: () => {
      addNotification("הגדרות ההתראות נשמרו", "success");
      qc.invalidateQueries({ queryKey: ["alerts-config"] });
      qc.invalidateQueries({ queryKey: ["alerts-eval"] });      // the evaluation ran against the old thresholds
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשמירת הגדרות ההתראות", "error"),
  });

  const draft = configDraft || config || {};
  const alerts = ev?.alerts || [];
  const hasAlerts = alerts.length > 0;

  return (
    <div className="max-w-4xl mx-auto space-y-5" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">מנוע התראות AI 🚨</h1>
          <p className="text-sm text-gray-500 mt-0.5">ניטור בריאות פעולות AI ויצירת קריאות אוטומטיות</p>
        </div>
        <button
          onClick={() => { setDispatchMsg(null); dispatchMutation.mutate(); }}
          disabled={dispatchMutation.isPending}
          className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {dispatchMutation.isPending ? "שולח..." : "בדוק ושלח"}
        </button>
      </div>

      {dispatchMsg && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-2 text-sm">{dispatchMsg}</div>
      )}

      {/* Evaluation summary */}
      {evLoading ? (
        <div className="bg-white rounded-xl border p-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : ev ? (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: "ריצות בחלון", value: ev.total_runs },
              { label: "שיעור כשל",  value: ev.failure_rate != null ? `${ev.failure_rate}%` : "—" },
              { label: "ממתינות",    value: ev.pending_count },
              { label: "עלות משוערת", value: ev.total_cost_usd != null ? `$${ev.total_cost_usd.toFixed(4)}` : "—" },
              { label: "ביטחון ממוצע", value: ev.avg_confidence != null ? `${ev.avg_confidence}%` : "—" },
              { label: "תקועות בהמתנה", value: ev.waiting_timeout_count ?? 0 },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white rounded-xl border p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-lg font-bold text-gray-800">{value}</p>
              </div>
            ))}
          </div>

          {/* The provenance of the `ביטחון ממוצע` tile above, drawn directly under the row it
              qualifies. */}
          <ConfidenceProvenance total={ev.confidence_analyses} degraded={ev.degraded_analyses} />

          {/* Cooldown notice */}
          {ev.cooldown_remaining_hours != null && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-600">
              Cooldown פעיל — ניתן לשלוח שוב בעוד {ev.cooldown_remaining_hours} שעות
              (נבדק {fmtDate(ev.evaluated_at)})
            </div>
          )}

          {/* Alert cards */}
          {hasAlerts ? (
            <div className="space-y-3">
              {alerts.map((a, i) => {
                const s = SEV[a.severity] || SEV.warning;
                return (
                  <div key={i} className={`rounded-xl border p-4 ${s.bg} ${s.border}`}>
                    <div className="flex items-start gap-3">
                      <span className="text-xl mt-0.5">{s.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${s.badge}`}>
                            {s.label}
                          </span>
                          <span className="text-xs text-gray-500 bg-white/60 border rounded px-1.5 py-0.5">
                            {TYPE_LABELS[a.type] || a.type}
                          </span>
                        </div>
                        <p className={`font-medium text-sm ${s.text}`}>{a.title}</p>
                        <p className={`text-xs mt-0.5 ${s.text} opacity-80`}>{a.detail}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          ערך: {a.value} / סף: {a.threshold}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
              <p className="text-2xl mb-2">✅</p>
              <p className="text-emerald-800 font-medium">אין התראות פעילות</p>
              <p className="text-sm text-emerald-700 opacity-75 mt-0.5">
                נבדק {fmtDate(ev.evaluated_at)} · חלון {ev.window_hours} שעות
              </p>
            </div>
          )}
        </>
      ) : evFailed ? (
        // a page with no summary during an outage must not read as "nothing to report"
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          לא ניתן לבדוק התראות כרגע — מצב המערכת אינו ידוע
        </div>
      ) : null}

      {/* Config */}
      {configFailed && !config && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          לא ניתן לטעון את הגדרות ההתראות
        </div>
      )}
      {!configLoading && config && (
        <div className="bg-white rounded-xl border p-5 space-y-4">
          <h2 className="font-semibold text-gray-700">הגדרות התראות</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { key: "failure_rate_pct",    label: "סף שיעור כשל (%)", min: 0, max: 100, step: 5 },
              { key: "pending_backlog",     label: "סף צבר ממתין",     min: 1, max: 9999 },
              { key: "cost_budget_usd",     label: "תקציב עלות (USD, 0=כבוי)", min: 0, max: 1000, step: 0.5 },
              { key: "low_confidence_pct",    label: "סף ביטחון AI נמוך (%)", min: 0, max: 100, step: 5 },
              { key: "min_runs_confidence",   label: "מינימום ריצות לבדיקת ביטחון", min: 1, max: 999 },
              { key: "waiting_timeout_hours", label: "סף המתנה לתגובה (שעות, 0=כבוי)", min: 0, max: 9999 },
              { key: "high_rejection_pct",    label: "סף שיעור דחייה (%)", min: 0, max: 100, step: 5 },
              { key: "window_hours",          label: "חלון זמן (שעות)",   min: 1, max: 720 },
              { key: "cooldown_hours",      label: "זמן קירור (שעות)", min: 0, max: 168 },
            ].map(({ key, label, min, max, step }) => (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                <input
                  type="number" min={min} max={max} step={step || 1}
                  value={draft[key] ?? config[key] ?? ""}
                  onChange={(e) => setConfigDraft((prev) => ({
                    ...(prev || config),
                    [key]: parseFloat(e.target.value) || 0,
                  }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            ))}
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.auto_dispatch ?? config.auto_dispatch ?? false}
              onChange={(e) => setConfigDraft((prev) => ({ ...(prev || config), auto_dispatch: e.target.checked }))}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-gray-700">שלח קריאות אוטומטיות בבדיקות מתוזמנות</span>
          </label>

          <div className="flex justify-end">
            <button
              onClick={() => configMutation.mutate(draft)}
              disabled={configMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {configMutation.isPending ? "שומר..." : "שמור הגדרות"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
